import { useState, useEffect, useCallback } from 'react'
import {
  Cloud, RefreshCw, CheckCircle, AlertTriangle, XCircle, Wrench, ExternalLink,
} from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

const STATUS_CONFIG: Record<string, { icon: React.ElementType; color: string; bg: string; dot: string; border: string; label: string }> = {
  operational:  { icon: CheckCircle,   color: 'text-emerald',      bg: 'bg-emerald/10', dot: 'bg-emerald', border: 'border-emerald/30', label: 'Operational' },
  degraded:     { icon: AlertTriangle, color: 'text-amber',        bg: 'bg-amber/10',   dot: 'bg-amber',   border: 'border-amber/30',   label: 'Degraded' },
  outage:       { icon: XCircle,       color: 'text-rose',         bg: 'bg-rose/10',    dot: 'bg-rose',     border: 'border-rose/30',    label: 'Outage' },
  maintenance:  { icon: Wrench,        color: 'text-blue',         bg: 'bg-blue/10',    dot: 'bg-blue',     border: 'border-blue/30',    label: 'Maintenance' },
  unknown:      { icon: Cloud,         color: 'text-text-muted',   bg: 'bg-surface-card', dot: 'bg-text-muted', border: 'border-border', label: 'Unknown' },
}

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

export default function CloudPage() {
  const [providers, setProviders] = useState<any[]>([])
  const [expandedProvider, setExpandedProvider] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const { data } = await api.get('/api/cloud/status')
      setProviders(data.providers || [])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData(); const i = setInterval(fetchData, 120_000); return () => clearInterval(i) }, [fetchData])

  const totalServices = providers.reduce((acc, p) => acc + (p.services?.length || 0), 0)
  const healthyServices = providers.reduce((acc, p) => acc + (p.services || []).filter((s: any) => s.status === 'operational').length, 0)
  const degradedServices = providers.reduce((acc, p) => acc + (p.services || []).filter((s: any) => s.status === 'degraded').length, 0)

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Cloud size={24} className="text-cyan" /> Cloud & M365 Status
          </h1>
          <p className="text-sm text-text-secondary mt-1">Live from AWS, Azure, GCP, and Microsoft 365 status feeds</p>
        </div>
        <button type="button" onClick={fetchData} className="btn-secondary text-xs">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* ══ COMPACT PROVIDER GRID ═══════════════════════════════════════════ */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-2">
        {providers.map(provider => {
          const s = STATUS_CONFIG[provider.overall_status] || STATUS_CONFIG.unknown
          const StatusIcon = s.icon
          const svcCount = (provider.services || []).length
          const healthyCount = (provider.services || []).filter((sv: any) => sv.status === 'operational').length
          const degradedCount = (provider.services || []).filter((sv: any) => sv.status === 'degraded').length
          const outageCount = (provider.services || []).filter((sv: any) => sv.status === 'outage').length
          const incidentCount = (provider.incidents || []).length

          return (
            <button
              key={provider.slug}
              type="button"
              onClick={() => setExpandedProvider(expandedProvider === provider.slug ? null : provider.slug)}
              className={clsx(
                'p-3 rounded-lg text-left transition-all duration-150 border',
                s.border, s.bg,
                expandedProvider === provider.slug && 'ring-2 ring-blue/40',
              )}
            >
              {/* Name + status on one line */}
              <div className="flex items-center gap-2 mb-1.5">
                <StatusIcon size={14} className={s.color} />
                <span className="text-xs font-semibold text-text-primary truncate flex-1">{provider.name}</span>
              </div>

              {/* Thin progress bar */}
              {svcCount > 0 && (
                <div className="flex gap-px h-1 rounded-full overflow-hidden bg-surface-hover">
                  {healthyCount > 0 && <div className="bg-emerald h-full" style={{ width: `${(healthyCount / svcCount) * 100}%` }} />}
                  {degradedCount > 0 && <div className="bg-amber h-full" style={{ width: `${(degradedCount / svcCount) * 100}%` }} />}
                  {outageCount > 0 && <div className="bg-rose h-full" style={{ width: `${(outageCount / svcCount) * 100}%` }} />}
                </div>
              )}

              {/* Compact stats */}
              <div className="mt-1 flex items-center gap-2 text-[10px]">
                <span className="text-emerald">{healthyCount}</span>
                {degradedCount > 0 && <span className="text-amber font-bold">{degradedCount} deg</span>}
                {outageCount > 0 && <span className="text-rose font-bold">{outageCount} out</span>}
                {incidentCount > 0 && <span className="text-rose-light">{incidentCount} inc</span>}
              </div>
            </button>
          )
        })}
      </div>

      {loading && !providers.length && (
        <div className="flex items-center justify-center h-32">
          <RefreshCw size={20} className="animate-spin text-text-muted" />
          <span className="ml-2 text-text-muted text-sm">Fetching cloud status...</span>
        </div>
      )}

      {/* ══ KPI BAR ═══════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-3 gap-4">
        <div className="stat-card">
          <span className="stat-label">Total Services</span>
          <span className="stat-value">{totalServices}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Healthy</span>
          <span className="stat-value text-emerald">{healthyServices}</span>
        </div>
        <div className="stat-card">
          <span className="stat-label">Issues</span>
          <span className={clsx('stat-value', degradedServices > 0 ? 'text-amber' : 'text-emerald')}>{degradedServices}</span>
        </div>
      </div>

      {/* ══ EXPANDED PROVIDER DETAIL ═══════════════════════════════════════════ */}
      {providers.filter(p => expandedProvider === p.slug).map(provider => {
        const overallStatus = STATUS_CONFIG[provider.overall_status] || STATUS_CONFIG.unknown

        return (
          <div key={provider.slug} className="card overflow-hidden">
            <div className="p-5 border-b border-border">
              <h2 className="text-lg font-bold text-text-primary">{provider.name} — Service Detail</h2>
            </div>

            {/* Incidents */}
            {(provider.incidents || []).length > 0 && (
              <div className="px-5 pt-4">
                <h3 className="text-xs font-semibold text-amber uppercase tracking-wider mb-2">Active Incidents</h3>
                {provider.incidents.slice(0, 5).map((inc: any, idx: number) => (
                  <div key={idx} className="p-3 rounded-lg bg-amber/5 border border-amber/20 mb-2">
                    <p className="text-sm font-medium text-text-primary">{inc.title}</p>
                    <p className="text-xs text-text-secondary mt-1">{inc.summary}</p>
                    {inc.url && (
                      <a href={inc.url} target="_blank" rel="noopener" className="inline-flex items-center gap-1 text-[10px] text-blue-light hover:underline mt-1">
                        <ExternalLink size={10} /> Details
                      </a>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Services grid */}
            <div className="p-5">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {(provider.services || []).map((service: any) => {
                  const ss = STATUS_CONFIG[service.status] || STATUS_CONFIG.unknown
                  const SvcIcon = ss.icon
                  return (
                    <div
                      key={service.name}
                      className={clsx('p-3 rounded-lg border-2', ss.border, ss.bg)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <SvcIcon size={16} className={ss.color} />
                        <span className="text-sm font-semibold text-text-primary">{service.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={clsx('text-xs font-medium', ss.color)}>{ss.label}</span>
                        {service.region && <span className="text-[10px] text-text-muted">{service.region}</span>}
                      </div>
                      {service.message && (
                        <p className="text-[11px] text-text-secondary mt-1.5 leading-relaxed">{service.message}</p>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
